import { useState } from 'react';
import { PROJEKT_WAEHRUNG, betrag, monatName, satzText, type AuswertungZeile, type BudgetStand } from '@beholder/shared/module/budget';
import { Karte } from '../../core/ui';
import { Balken, Kreis, PlanLeiste, Saeulen } from './Diagramme';
import { pfad } from './hilfe';

type Sicht = 'brutto' | 'netto';

const ist = (z: AuswertungZeile, v: Sicht) => (v === 'brutto' ? z.istCent : z.istNettoCent);
const angebote = (z: AuswertungZeile, v: Sicht) => (v === 'brutto' ? z.angeboteCent : z.angeboteNettoCent);
const ein = (z: AuswertungZeile, v: Sicht) => (v === 'brutto' ? z.einnahmenCent : z.einnahmenNettoCent);

/** Auswertung je Kategorie, Person, Label und Monat – selbst gezeichnet, wahlweise brutto oder netto – plus Steuer-Summen und Export. */
export function Auswertung({ projektId, s }: { projektId: string; s: BudgetStand }) {
  const [sicht, setSicht] = useState<Sicht>('brutto');
  const w = PROJEKT_WAEHRUNG;
  const a = s.auswertung;
  const g = s.gesamt;
  const oben = s.kategorien.filter((k) => k.tiefe === 0);
  const ausgabenGesamt = sicht === 'brutto' ? g.istCent : g.istNettoCent;
  const einnahmenGesamt = sicht === 'brutto' ? g.einnahmen.gesamtCent : g.einnahmen.nettoCent;
  const steuerAus = a.steuer.filter((x) => x.richtung === 'ausgabe');
  const steuerEin = a.steuer.filter((x) => x.richtung === 'einnahme');
  const summe = (l: typeof a.steuer, f: 'nettoCent' | 'steuerCent' | 'bruttoCent') => l.reduce((x, y) => x + y[f], 0);

  return (
    <div className="stapel">
      <div className="bu-werkzeug">
        <div className="bu-schalter" role="tablist" aria-label="Beträge">
          {(['brutto', 'netto'] as const).map((x) => (
            <button key={x} type="button" role="tab" aria-selected={sicht === x} className={sicht === x ? 'aktiv' : ''} onClick={() => setSicht(x)}>
              {x === 'brutto' ? 'Brutto' : 'Netto'}
            </button>
          ))}
        </div>
        <span className="leise klein">
          {sicht === 'netto' ? 'Netto: ohne Mehrwertsteuer. Belege ohne Steuerangabe zählen mit ihrem vollen Betrag.' : 'Brutto: mit Mehrwertsteuer, so wie bezahlt.'}
        </span>
      </div>

      <Karte titel={`Einnahmen und Ausgaben (${sicht})`}>
        <div className="bu-kennzahlen bu-kennzahlen-3">
          <div className="bu-kennzahl">
            <span className="bu-kennzahl-titel">Einnahmen</span>
            <strong className="bu-kennzahl-wert bu-text-ok">{betrag(einnahmenGesamt, w)}</strong>
          </div>
          <div className="bu-kennzahl">
            <span className="bu-kennzahl-titel">Ausgaben</span>
            <strong className="bu-kennzahl-wert">{betrag(ausgabenGesamt, w)}</strong>
          </div>
          <div className="bu-kennzahl">
            <span className="bu-kennzahl-titel">Saldo</span>
            <strong className={`bu-kennzahl-wert${einnahmenGesamt - ausgabenGesamt < 0 ? ' bu-text-gefahr' : ''}`}>{betrag(einnahmenGesamt - ausgabenGesamt, w)}</strong>
          </div>
        </div>
      </Karte>

      <div className="bu-raster-auswertung">
        <Karte titel="Ausgaben je Kategorie">
          <Kreis teile={a.jeKategorie.map((z) => ({ schluessel: z.schluessel || '__ohne', name: z.name, wert: ist(z, sicht) }))} waehrung={w} />
        </Karte>
        <Karte titel="Ausgaben je Person">
          <p className="leise klein bu-ohne-abstand">Wer hat was ausgegeben bzw. angefragt (schraffiert: offene Angebote). ° = ohne Beholder-Konto.</p>
          <Balken
            zeilen={a.jeTeilnehmer
              .filter((z) => z.istCent || z.angeboteCent)
              .map((z) => ({ schluessel: z.schluessel || '__ohne', name: `${z.name}${z.frei ? ' °' : ''}`, wert: ist(z, sicht), zusatz: angebote(z, sicht) }))}
            waehrung={w}
          />
        </Karte>
        {a.jeLabel.length > 0 && (
          <Karte titel="Einnahmen je Label">
            <p className="leise klein bu-ohne-abstand">Eine Einnahme mit mehreren Labels zählt bei jedem davon mit.</p>
            <Balken
              zeilen={a.jeLabel.map((z) => ({ schluessel: z.schluessel || '__ohne', name: z.name, wert: ein(z, sicht) }))}
              waehrung={w}
            />
          </Karte>
        )}
        {a.jeTeilnehmer.some((z) => z.einnahmenCent) && (
          <Karte titel="Einnahmen je Person">
            <Balken
              zeilen={a.jeTeilnehmer
                .filter((z) => z.einnahmenCent)
                .sort((x, y) => ein(y, sicht) - ein(x, sicht))
                .map((z) => ({ schluessel: z.schluessel || '__ohne', name: `${z.name}${z.frei ? ' °' : ''}`, wert: ein(z, sicht) }))}
              waehrung={w}
            />
          </Karte>
        )}
      </div>

      <Karte titel="Je Monat">
        <Saeulen
          zeilen={a.jeMonat.map((z) => ({ schluessel: z.schluessel, name: monatName(z.schluessel), wert: ist(z, sicht), zweit: ein(z, sicht) }))}
          waehrung={w}
        />
      </Karte>

      <Karte titel="Mehrwertsteuer je Satz">
        {a.steuer.length === 0 ? (
          <p className="leise">Noch keine Rechnungen oder Einnahmen.</p>
        ) : (
          <div className="tabelle-rahmen">
            <table className="tabelle">
              <thead>
                <tr>
                  <th>Satz</th>
                  <th className="bu-rechts">Netto</th>
                  <th className="bu-rechts">Steuer</th>
                  <th className="bu-rechts">Brutto</th>
                  <th className="bu-rechts">Belege</th>
                </tr>
              </thead>
              {[
                { titel: 'Ausgaben (Vorsteuer)', zeilen: steuerAus },
                { titel: 'Einnahmen (Umsatzsteuer)', zeilen: steuerEin },
              ]
                .filter((x) => x.zeilen.length)
                .map((gruppe) => (
                  <tbody key={gruppe.titel}>
                    <tr className="bu-tabelle-gruppe">
                      <td colSpan={5}>{gruppe.titel}</td>
                    </tr>
                    {gruppe.zeilen.map((x) => (
                      <tr key={`${x.richtung}${x.satzBp}`}>
                        <td className={x.satzBp === null ? 'leise' : ''}>{satzText(x.satzBp)}</td>
                        <td className="bu-rechts">{betrag(x.nettoCent, w)}</td>
                        <td className="bu-rechts">{x.satzBp === null ? '–' : betrag(x.steuerCent, w)}</td>
                        <td className="bu-rechts">{betrag(x.bruttoCent, w)}</td>
                        <td className="bu-rechts">{x.anzahl}</td>
                      </tr>
                    ))}
                    <tr className="bu-tabelle-summe">
                      <td>Summe</td>
                      <td className="bu-rechts">{betrag(summe(gruppe.zeilen, 'nettoCent'), w)}</td>
                      <td className="bu-rechts">{betrag(summe(gruppe.zeilen, 'steuerCent'), w)}</td>
                      <td className="bu-rechts">{betrag(summe(gruppe.zeilen, 'bruttoCent'), w)}</td>
                      <td />
                    </tr>
                  </tbody>
                ))}
            </table>
          </div>
        )}
        <p className="leise klein bu-ohne-abstand">
          „ohne Steuerangabe“: Belege, bei denen die Mehrwertsteuer nicht aufgeschlüsselt ist – sie zählen netto mit dem vollen Betrag. Nur eine Übersicht, keine
          Steuerberatung.
        </p>
      </Karte>

      <Karte titel="Plan gegen Ist je Kategorie (brutto)">
        {oben.length === 0 ? (
          <p className="leise">Noch keine Kategorien.</p>
        ) : (
          <ul className="bu-plan-liste">
            {oben.map((k) => (
              <li key={k.id}>
                <div className="reihe">
                  <strong>{k.name}</strong>
                  <span className="abstand" />
                  <span>
                    {betrag(k.istCent, w)} <span className="leise">/ {k.planCent === null ? 'ohne Budget' : betrag(k.planCent, w)}</span>
                    {k.prozent !== null && <span className={`klein ${k.warnung === 'ueberschritten' ? 'bu-text-gefahr' : 'leise'}`}> · {k.prozent} %</span>}
                  </span>
                </div>
                <PlanLeiste planCent={k.planCent} istCent={k.istCent} angeboteCent={k.angeboteCent} warnung={k.warnung} waehrung={w} />
              </li>
            ))}
          </ul>
        )}
      </Karte>

      <Karte titel="Tabelle (brutto)">
        <div className="tabelle-rahmen">
          <table className="tabelle">
            <thead>
              <tr>
                <th>Kategorie</th>
                <th className="bu-rechts">Budget</th>
                <th className="bu-rechts">Ausgegeben</th>
                <th className="bu-rechts">bezahlt</th>
                <th className="bu-rechts">offen</th>
                <th className="bu-rechts">Angebote</th>
                <th className="bu-rechts">Rest</th>
              </tr>
            </thead>
            <tbody>
              {s.kategorien.map((k) => (
                <tr key={k.id}>
                  <td style={{ paddingLeft: 12 + k.tiefe * 16 }}>{k.name}</td>
                  <td className="bu-rechts">{betrag(k.planCent, w)}</td>
                  <td className="bu-rechts">{betrag(k.istCent, w)}</td>
                  <td className="bu-rechts">{betrag(k.bezahltCent, w)}</td>
                  <td className="bu-rechts">{betrag(k.offenCent, w)}</td>
                  <td className="bu-rechts">{betrag(k.angeboteCent, w)}</td>
                  <td className={`bu-rechts${k.restCent !== null && k.restCent < 0 ? ' bu-text-gefahr' : ''}`}>{betrag(k.restCent, w)}</td>
                </tr>
              ))}
              <tr className="bu-tabelle-summe">
                <td>Gesamt</td>
                <td className="bu-rechts">{betrag(g.planCent, w)}</td>
                <td className="bu-rechts">{betrag(g.istCent, w)}</td>
                <td className="bu-rechts">{betrag(g.bezahltCent, w)}</td>
                <td className="bu-rechts">{betrag(g.offenCent, w)}</td>
                <td className="bu-rechts">{betrag(g.angeboteCent, w)}</td>
                <td className={`bu-rechts${g.restCent !== null && g.restCent < 0 ? ' bu-text-gefahr' : ''}`}>{betrag(g.restCent, w)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Karte>

      <Karte titel="Export">
        <p className="leise bu-ohne-abstand">
          Alle Belege (brutto, netto, Steuer, Original und Euro), Kategorien, Einnahmen, Steuer-Summen, Personen und Monate – zum Weitergeben oder Weiterrechnen.
        </p>
        <div className="reihe">
          <a className="knopf knopf-haupt" href={pfad(projektId, '/export?format=xlsx')} download>
            Excel (.xlsx)
          </a>
          <a className="knopf" href={pfad(projektId, '/export?format=csv&tabelle=belege')} download>
            CSV: Belege
          </a>
          <a className="knopf" href={pfad(projektId, '/export?format=csv&tabelle=kategorien')} download>
            CSV: Kategorien
          </a>
        </div>
      </Karte>
    </div>
  );
}
